// src/pages/help/CreateSupportTicketModal.tsx

import React, { useState } from "react";
import { Modal, Form, Input, Select, Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../utils/auth/sessionUser";
import type { UploadFile } from "antd/es/upload/interface";
import { createSupportTicket } from "../../api/supportTickets/supportTicketsApi";
import { User } from "../../types/admin/UserTypes";
import { fetchClinicCompany } from "../../api/admin/companyApi";
import { useNotifications } from "../../context/NotificationContext";

interface CreateSupportTicketModalProps {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

const CreateSupportTicketModal: React.FC<CreateSupportTicketModalProps> = ({ open, onClose, onSuccess }) => {
	const { showSuccess, showError } = useNotifications();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [fileList, setFileList] = useState<UploadFile[]>([]);

	const user:User = getSessionUser();

	const handleSubmit = async (values: any) => {
		setLoading(true);
		try {
			const files = fileList.map((f) => f.originFileObj as File);
			let company = await fetchClinicCompany();
			await createSupportTicket({
				user_id: user.id,
				company_id: company.id,
				subject: values.subject,
				description: values.description,
				priority: values.priority || "normal",
				category: values.category,
				attachments: files.length > 0 ? files : undefined,
			});

			showSuccess("Support ticket created successfully");
			form.resetFields();
			setFileList([]);
			onSuccess();
		} catch (error) {
			showError("Failed to create support ticket");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setFileList([]);
		onClose();
	};

	return (
		<Modal
			title="Create Support Ticket"
			open={open}
			onCancel={handleCancel}
			footer={[
				<Button key="cancel" onClick={handleCancel} disabled={loading}>
					Cancel
				</Button>,
				<Button key="submit" type="primary" onClick={() => form.submit()} loading={loading}>
					Submit Ticket
				</Button>,
			]}
			width={700}
		>
			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				<Form.Item name="subject" label="Subject" rules={[{ required: true, message: "Please enter a subject" }]}>
					<Input placeholder="Brief description of your issue" maxLength={255} />
				</Form.Item>

				<Form.Item name="description" label="Description" rules={[{ required: true, message: "Please provide details" }]}>
					<Input.TextArea placeholder="Please provide detailed information about your issue" rows={6} maxLength={5000} showCount />
				</Form.Item>

				<Form.Item name="priority" label="Priority" initialValue="normal">
					<Select>
						<Select.Option value="low">Low</Select.Option>
						<Select.Option value="normal">Normal</Select.Option>
						<Select.Option value="high">High</Select.Option>
						<Select.Option value="urgent">Urgent</Select.Option>
					</Select>
				</Form.Item>

				<Form.Item name="category" label="Category (Optional)">
					<Input placeholder="e.g., Billing, Technical, Feature Request" maxLength={100} />
				</Form.Item>

				<Form.Item label="Attachments (Optional)">
					<Upload fileList={fileList} onChange={({ fileList }) => setFileList(fileList)} beforeUpload={() => false} maxCount={10} accept="image/*,.pdf,.doc,.docx,.txt,.log">
						<Button icon={<UploadOutlined />}>Add Files (Max 10, 10MB each)</Button>
					</Upload>
					<div className="text-xs text-gray-500 mt-2">Supported: Images, PDFs, Documents, Text files, Log files</div>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default CreateSupportTicketModal;
